/* Visual enhancement only. Product, cart, search and navigation remain Shopify/Dawn. */
(() => {
  if (window.rs2DesignInitialized) return;
  window.rs2DesignInitialized = true;
  const root = document.documentElement;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const scheme = matchMedia('(prefers-color-scheme: dark)');
  const activeEffects = new Set();
  let stopEntry = () => {};
  const ranks = [0,8,2,10,12,4,14,6,3,11,1,9,15,7,13,5];
  // Decode the complete Bayer palette once; never create image URLs during a fade.
  const masks = Array.from({length: 17}, (_, level) => {
    const cells = ranks.map((rank, i) => rank < level ? `<rect x="${i % 4}" y="${Math.floor(i / 4)}" width="1" height="1"/>` : '').join('');
    const image = new Image();
    image.src = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4" shape-rendering="crispEdges" fill="white">${cells}</svg>`)}`;
    return {image, css: `url("${image.src}")`};
  });
  const masksReady = Promise.all(masks.map(({image}) => Promise.resolve().then(() => image.decode()))).then(() => true, () => false);
  const maskProperties = ['', '-webkit-'].flatMap(prefix => ['image', 'size', 'repeat', 'position'].map(name => `${prefix}mask-${name}`));
  function veil(host, viewport = false, color) {
    const element = document.createElement('span');
    element.className = `rs2-dither-veil${viewport ? ' rs2-dither-veil--viewport' : ''}`;
    element.setAttribute('aria-hidden', 'true');
    if (color) element.style.setProperty('--rs2-veil-color', color);
    host.append(element);
    return element;
  }
  function ditherFade(target, from, to, duration, done = () => {}) {
    const saved = [...maskProperties, 'opacity', 'will-change'].map(name => [name, target.style.getPropertyValue(name), target.style.getPropertyPriority(name)]);
    let frame = 0, watchdog = 0, stopped = false, last = -1, level = from;
    const restore = () => saved.forEach(([name, value, priority]) => value ? target.style.setProperty(name, value, priority) : target.style.removeProperty(name));
    const cleanup = (complete) => {
      if (stopped) return;
      stopped = true;
      clearTimeout(watchdog);
      cancelAnimationFrame(frame);
      activeEffects.delete(finish);
      restore();
      if (complete) done();
    };
    const finish = () => cleanup(true);
    const cancel = () => cleanup(false);
    cancel.level = () => level;
    if (motion.matches || document.hidden || typeof CSS === 'undefined' || !CSS.supports('mask-image', masks[8].css) && !CSS.supports('-webkit-mask-image', masks[8].css)) {
      finish();
      return cancel;
    }
    activeEffects.add(finish);
    // Hold an incoming layer before its first paint, including a cold palette cache.
    if (from === 0) target.style.setProperty('opacity', '0');
    watchdog = setTimeout(finish, duration + 250);
    masksReady.then(ready => {
      if (stopped) return;
      if (!ready || document.hidden || motion.matches) { finish(); return; }
      try {
        for (const prefix of ['', '-webkit-']) {
          target.style.setProperty(`${prefix}mask-size`, '8px 8px');
          target.style.setProperty(`${prefix}mask-repeat`, 'repeat');
          target.style.setProperty(`${prefix}mask-position`, '0 0');
        }
        const paint = value => {
          level = value;
          const step = Math.max(0, Math.min(16, Math.round(value * 16)));
          if (step === last) return;
          last = step;
          target.style.setProperty('mask-image', masks[step].css);
          target.style.setProperty('-webkit-mask-image', masks[step].css);
        };
        paint(from);
        const opacity = saved.find(([name]) => name === 'opacity');
        if (opacity[1]) target.style.setProperty('opacity', opacity[1], opacity[2]);
        else target.style.removeProperty('opacity');
        target.style.setProperty('will-change', 'mask-image');
        const start = performance.now();
        const tick = now => {
          if (stopped) return;
          const progress = Math.min(1, Math.max(0, (now - start) / duration));
          paint(from + (to - from) * progress);
          if (progress < 1) frame = requestAnimationFrame(tick);
          else finish();
        };
        frame = requestAnimationFrame(tick);
      } catch (_) { finish(); }
    });
    return cancel;
  }
  window.rs2DitherFade = ditherFade;
  const reveals = new Map();
  window.rs2DitherReveal = (target, duration = 520) => {
    if (!target?.style) return () => {};
    reveals.get(target)?.();
    let stop = () => {}, cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      stop();
      reveals.delete(target);
    };
    reveals.set(target, cleanup);
    stop = ditherFade(target, 0, 1, Number.isFinite(duration) && duration > 0 ? duration : 520, cleanup);
    return cleanup;
  };
  let preference = root.dataset.rsPreference || 'dark';
  try { preference = localStorage.getItem('rs-appearance') || preference; } catch (_) {}
  if (!['light','dark','system'].includes(preference)) preference = 'dark';
  let stopTheme = () => {};
  function syncAppearance(animate = false) {
    stopTheme();
    const next = preference === 'system' ? (scheme.matches ? 'dark' : 'light') : preference;
    const animateTheme = animate && !motion.matches && !document.hidden && next !== root.dataset.rsTheme;
    const previousColor = animateTheme ? getComputedStyle(document.body).backgroundColor : null;
    root.dataset.rsPreference = preference;
    root.dataset.rsTheme = next;
    root.style.colorScheme = next;
    document.querySelectorAll('[data-rs-appearance]').forEach(el => { el.value = preference; });
    document.querySelectorAll('[data-rs2-appearance-toggle]').forEach(button => {
      const light = next === 'light';
      button.setAttribute('aria-pressed', String(light));
      button.setAttribute('aria-label', light ? (button.dataset.darkLabel || 'Dark mode') : (button.dataset.lightLabel || 'Light mode'));
      const image = button.querySelector('img[data-sun-src][data-moon-src]');
      if (image) image.src = light ? image.dataset.moonSrc : image.dataset.sunSrc;
      button.querySelectorAll('[data-rs2-sun]').forEach(el => { el.hidden = light; });
      button.querySelectorAll('[data-rs2-moon]').forEach(el => { el.hidden = !light; });
    });
    if (!animateTheme) return;
    const texture = veil(document.body, true, previousColor);
    const stop = ditherFade(texture, 1, 0, 440, () => texture.remove());
    stopTheme = () => { stop(); texture.remove(); };
  }
  function chooseAppearance(value) { preference = value; try { localStorage.setItem('rs-appearance', value); } catch (_) {} syncAppearance(true); }
  document.addEventListener('click', event => { if (event.target.closest('[data-rs2-appearance-toggle]')) chooseAppearance(root.dataset.rsTheme === 'dark' ? 'light' : 'dark'); });
  document.addEventListener('change', event => { if (event.target.matches('[data-rs-appearance]') && ['dark','light','system'].includes(event.target.value)) chooseAppearance(event.target.value); });
  scheme.addEventListener('change', () => { if (preference === 'system') syncAppearance(true); });
  window.addEventListener('storage', event => { if (event.key !== 'rs-appearance' && event.key !== null) return; preference = ['dark','light','system'].includes(event.newValue) ? event.newValue : 'dark'; syncAppearance(true); });
  const mounted = new WeakMap();
  function scrollCarousel(host) {
    const track = host.querySelector('[data-rs2-track]');
    const slides = [...host.querySelectorAll('[data-rs2-slide]')];
    if (!track || !slides.length) return () => {};
    const buttons = [...host.querySelectorAll('[data-rs2-slide-to]')];
    const heading = host.querySelector('[data-rs2-hero-heading]');
    const action = host.querySelector('[data-rs2-hero-action]');
    const previous = host.querySelector('[data-rs2-slide-prev]');
    const next = host.querySelector('[data-rs2-slide-next]');
    const status = host.querySelector('[data-rs2-status]');
    const controller = new AbortController(), options = {signal:controller.signal};
    const effects = new Map();
    let current = 0, frame = 0, timer = 0, drag = null, suppressClick = false;
    let revealed = new Set([0]);
    const nearest = () => slides.reduce((best, slide, index) =>
      Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[best].offsetLeft - track.scrollLeft) ? index : best, 0);
    function update(index) {
      const slide = slides[index];
      current = index;
      heading.textContent = slide.dataset.heroHeading;
      action.textContent = slide.dataset.heroAction;
      const url = slide.dataset.heroUrl.replace(/^\/en(?=\/|$)/, '');
      action.href = (/^\/en(?:\/|$)/.test(window.location.pathname) ? '/en' : '') + url;
      buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      previous.disabled = index === 0;
      next.disabled = index === slides.length - 1;
      status.textContent = buttons[index]?.getAttribute('aria-label') || '';
      slides.slice(Math.max(0,index - 1), index + 2).forEach(item => {
        const image = item.querySelector('img');
        if (image) image.loading = 'eager';
      });
    }
    function paint() {
      frame = 0;
      for (const [index, slide] of slides.entries()) {
        const left = slide.offsetLeft - track.scrollLeft;
        const visible = Math.min(track.clientWidth, left + slide.clientWidth) - Math.max(0, left);
        const image = slide.querySelector('img');
        if (revealed.has(index) || visible < slide.clientWidth * .2 || !image?.complete || !image.naturalWidth) continue;
        revealed.add(index);
        effects.get(index)?.();
        // A partial pixel reveal travels with the incoming image; scrolling never waits for it.
        effects.set(index, ditherFade(image, .35, 1, 360, () => effects.delete(index)));
      }
      const index = nearest();
      if (index !== current) update(index);
    }
    function settle() {
      clearTimeout(timer);
      if (drag) return;
      paint();
      revealed = new Set([current]);
    }
    function select(index, smooth = true) {
      index = Math.max(0, Math.min(slides.length - 1, index));
      track.scrollTo({left:slides[index].offsetLeft, behavior:smooth && !motion.matches ? 'smooth' : 'instant'});
    }
    track.addEventListener('scroll', () => {
      if (!frame) frame = requestAnimationFrame(paint);
      clearTimeout(timer);
      timer = setTimeout(settle, 160);
    }, {...options, passive:true});
    track.addEventListener('scrollend', settle, options);
    buttons.forEach((button, index) => button.addEventListener('click', () => select(index), options));
    previous.addEventListener('click', () => select(current - 1), options);
    next.addEventListener('click', () => select(current + 1), options);
    host.addEventListener('keydown', event => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input, select, textarea') ||
          !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      event.preventDefault();
      select(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1));
    }, options);
    // Touch and trackpad use the browser's native scrolling. Only a mouse needs drag handling.
    track.addEventListener('pointerdown', event => {
      suppressClick = false;
      if (event.pointerType !== 'mouse' || !event.isPrimary || event.button !== 0) return;
      drag = {id:event.pointerId, x:event.clientX, y:event.clientY, left:track.scrollLeft, active:false};
    }, options);
    track.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.active && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.25) {
        drag.active = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      }
      if (!drag.active) return;
      event.preventDefault();
      track.scrollLeft = drag.left - dx;
    }, options);
    const endDrag = event => {
      if (!drag || event.pointerId !== drag.id) return;
      const moved = drag.active;
      drag = null;
      track.classList.remove('is-dragging');
      if (moved) { suppressClick = true; select(nearest()); }
    };
    track.addEventListener('pointerup', endDrag, options);
    track.addEventListener('pointercancel', endDrag, options);
    track.addEventListener('lostpointercapture', endDrag, options);
    track.addEventListener('dragstart', event => event.preventDefault(), options);
    track.addEventListener('click', event => {
      if (!suppressClick) return;
      event.preventDefault(); event.stopPropagation(); suppressClick = false;
    }, {...options,capture:true});
    window.addEventListener('resize', () => select(current, false), options);
    update(0);
    return () => {
      controller.abort(); clearTimeout(timer); cancelAnimationFrame(frame);
      effects.forEach(cancel => cancel());
      track.classList.remove('is-dragging');
    };
  }
  function carousel(host) {
    if (host.hasAttribute('data-rs2-scroll-carousel')) return scrollCarousel(host);
    const slides = [...host.querySelectorAll('[data-rs2-slide]')];
    const buttons = [...host.querySelectorAll('[data-rs2-slide-to]')];
    const pause = host.querySelector('[data-rs2-pause]');
    let gesture = null, suppressClick = false, transitioning = false, queued = null, destroyed = false, suspended = false;
    const controller = new AbortController(), options = {signal:controller.signal};
    const failedImages = new Set();
    let wheelDistance = 0, wheelCommitted = false, wheelTimer = 0;
    let loadController;
    let pendingAutomatic = false, sequence = 0, current = Math.max(0, slides.findIndex(slide => !slide.hidden)), timer = 0, paused = false, hovered = false, focused = false, cancel = () => {};
    function schedule() {
      clearTimeout(timer);
      if (host.dataset.rs2Autoplay === 'false' || paused || hovered || focused || gesture || transitioning || suspended || destroyed || motion.matches || document.hidden) {
        if (pendingAutomatic) { ++sequence; pendingAutomatic = false; loadController?.abort(); }
        return;
      }
      for (let offset = 1; offset < slides.length; offset++) {
        const next = (current + offset) % slides.length;
        if (!failedImages.has(next)) { timer = setTimeout(() => select(next),6000); break; }
      }
    }
    async function select(index, manual = false) {
      if (!Number.isInteger(index) || index < 0 || index >= slides.length) return;
      if (destroyed || suspended) return;
      if (transitioning) {
        queued = index === current ? null : {index, manual};
        return;
      }
      const ticket = ++sequence;
      loadController?.abort();
      if (index === current) { pendingAutomatic = false; schedule(); return; }
      const requestLoad = new AbortController();
      loadController = requestLoad;
      pendingAutomatic = !manual;
      clearTimeout(timer);
      const slide = slides[index];
      const images = [...slide.querySelectorAll('img')];
      images.forEach(image => { image.loading = 'eager'; });
      const decoded = await new Promise(resolve => {
        const loaded = images.map(() => false);
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          clearTimeout(deadline);
          requestLoad.signal.removeEventListener('abort', finish);
          resolve(loaded.slice());
        };
        const deadline = setTimeout(finish, 1500);
        requestLoad.signal.addEventListener('abort', finish, {once: true});
        Promise.all(images.map(async (image, i) => {
          try { await image.decode(); loaded[i] = true; } catch (_) {}
        })).then(finish);
      });
      if (ticket !== sequence) return;
      pendingAutomatic = false;
      if (!manual && (paused || hovered || focused || motion.matches || document.hidden)) return;
      if (decoded.every(Boolean)) failedImages.delete(index);
      else {
        failedImages.add(index);
        if (!manual) { schedule(); return; }
      }
      // A broken photo must not make a manually selected category inaccessible.
      images.forEach((image,i) => { image.style.visibility = decoded[i] ? '' : 'hidden'; });
      cancel();
      const previous = slides[current];
      current = index;
      slides.forEach((item,i) => {
        item.hidden = i !== current && item !== previous;
        item.inert = i !== current;
        item.style.zIndex = i === current ? '1' : '0';
        // Each visible layer retains its own colours during the Bayer dissolve.
        for (const [name,key] of [['--category-bg','categoryBg'],['--category-top','categoryTop']]) item.style.setProperty(name,item.dataset[key]);
        item.style.setProperty('--category-fg',item.dataset.categoryLight === 'true' ? '#000' : '#fff');
      });
      if (manual) { const status = host.querySelector('[data-rs2-status]'); if (status) status.textContent = buttons.find(button => Number(button.dataset.rs2SlideTo) === current)?.getAttribute('aria-label') || ''; }
      for (const name of ['--category-bg','--category-fg','--category-top']) { const key = {'--category-bg':'categoryBg','--category-fg':'categoryFg','--category-top':'categoryTop'}[name]; const value = slide.dataset[key] || slide.style.getPropertyValue(name); if (value) host.style.setProperty(name,value); }
      host.dataset.categoryLight = slide.dataset.categoryLight || 'false';
      buttons.forEach(button => button.setAttribute('aria-pressed',String(Number(button.dataset.rs2SlideTo) === current)));
      const cleanup = () => {
        slides.forEach(item => { item.hidden = item !== slide; });
        transitioning = false;
        cancel = () => {};
        const next = queued;
        queued = null;
        if (next && !document.hidden && !suspended && !destroyed) select(next.index, next.manual);
        else schedule();
      };
      transitioning = true;
      const stop = ditherFade(slide, 0, 1, 480, cleanup);
      cancel = () => { stop(); slides.forEach(item => { item.hidden = item !== slide; }); transitioning = false; };
    }
    host.addEventListener('pointerdown',event => {
      suppressClick = false;
      if (!event.isPrimary || event.button !== 0 || event.target.closest('button, input, select, textarea')) return;
      gesture = {id:event.pointerId, x:event.clientX, y:event.clientY, horizontal:false};
      schedule();
    },options);
    host.addEventListener('pointermove',event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.25) {
        gesture.horizontal = true;
        host.setPointerCapture(event.pointerId);
        event.preventDefault();
      }
    },options);
    host.addEventListener('pointerup',event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      const swiped = gesture.horizontal && Math.abs(dx) >= 36 && Math.abs(dx) > Math.abs(dy) * 1.25;
      gesture = null;
      if (swiped) {
        suppressClick = true;
        select(((queued?.index ?? current) + (dx < 0 ? 1 : -1) + slides.length) % slides.length,true);
      } else schedule();
    },options);
    const cancelGesture = () => { gesture = null; schedule(); };
    host.addEventListener('pointercancel',cancelGesture,options);
    host.addEventListener('lostpointercapture',cancelGesture,options);
    host.addEventListener('dragstart',event => { if (gesture) event.preventDefault(); },options);
    host.addEventListener('click',event => {
      if (!suppressClick) return;
      event.preventDefault(); event.stopPropagation(); suppressClick = false;
    },{...options,capture:true});
    buttons.forEach(button => button.addEventListener('click',() => select(Number(button.dataset.rs2SlideTo),true),options));
    const move = delta => select(((queued?.index ?? current) + delta + slides.length) % slides.length, true);
    host.querySelector('[data-rs2-slide-prev]')?.addEventListener('click', () => move(-1), options);
    host.querySelector('[data-rs2-slide-next]')?.addEventListener('click', () => move(1), options);
    // Trackpads emit wheel events; one burst, including its inertia, advances one product.
    host.addEventListener('wheel', event => {
      if (event.defaultPrevented || event.ctrlKey || gesture || suspended || destroyed ||
          event.target.closest('input, select, textarea, [contenteditable="true"]') ||
          Math.abs(event.deltaX) <= Math.abs(event.deltaY) * 1.25) return;
      event.preventDefault();
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => { wheelDistance = 0; wheelCommitted = false; }, 240);
      if (wheelCommitted) return;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? host.clientWidth : 1;
      wheelDistance += event.deltaX * unit;
      if (Math.abs(wheelDistance) < 36) return;
      wheelCommitted = true;
      move(wheelDistance > 0 ? 1 : -1);
    }, {...options, passive:false});
    host.addEventListener('keydown', event => {
      if (host.dataset.rs2Autoplay !== 'false' || event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input, select, textarea')) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      move(event.key === 'ArrowRight' ? 1 : -1);
    }, options);
    pause?.addEventListener('click',() => { paused = !paused; pause.setAttribute('aria-pressed',String(paused)); pause.textContent = paused ? (pause.dataset.labelPlay || pause.dataset.playLabel || 'Play') : (pause.dataset.labelPause || pause.dataset.pauseLabel || 'Pause'); schedule(); },options);
    host.addEventListener('pointerenter',event => { if (event.pointerType === 'mouse') { hovered = true; schedule(); } },options);
    host.addEventListener('pointerleave',() => { hovered = false; if (gesture && !gesture.horizontal) gesture = null; schedule(); },options);
    host.addEventListener('focusin',() => { focused = true; schedule(); },options);
    host.addEventListener('focusout',event => { focused = host.contains(event.relatedTarget); schedule(); },options);
    document.addEventListener('visibilitychange',schedule,options);
    window.addEventListener('pagehide', () => {
      suspended = true; queued = null; ++sequence; loadController?.abort(); pendingAutomatic = false;
      clearTimeout(wheelTimer); wheelDistance = 0; wheelCommitted = false;
      clearTimeout(timer); cancel();
    }, options);
    window.addEventListener('pageshow', () => { suspended = false; schedule(); }, options);
    motion.addEventListener('change',schedule,options);
    schedule();
    return () => { destroyed = true; queued = null; ++sequence; loadController?.abort(); controller.abort(); clearTimeout(timer); clearTimeout(wheelTimer); cancel(); };
  }
  function stitch(host) {
    const detail = host.querySelector('[data-rs2-stitch-image]');
    const button = host.querySelector('[data-rs2-stitch-toggle]');
    if (!detail) return () => {};
    const controller = new AbortController(), options = {signal:controller.signal};
    let hover = false, focused = false, pinned = false, sequence = 0, cancel = () => {};
    detail.hidden = true;
    async function update() {
      const ticket = ++sequence, wanted = hover || focused || pinned;
      const level = detail.hidden ? 0 : (cancel.level?.() ?? 1);
      cancel(); button?.setAttribute('aria-pressed',String(wanted));
      if (wanted) {
        detail.loading = 'eager';
        try { if (!detail.complete || !detail.naturalWidth) await detail.decode(); } catch (_) { if (ticket === sequence) { button?.setAttribute('aria-pressed','false'); detail.hidden = true; } return; }
        if (ticket !== sequence) return;
        detail.hidden = false;
      }
      cancel = ditherFade(detail, Number.isFinite(level) ? level : 0, wanted ? 1 : 0, 420, () => { detail.hidden = !wanted; });
    }
    host.addEventListener('pointerenter',event => { if (event.pointerType === 'mouse') { hover = true; update(); } },options);
    host.addEventListener('pointerleave',() => { hover = false; update(); },options);
    host.addEventListener('focusin',event => { if (!button?.contains(event.target)) { focused = true; update(); } },options);
    host.addEventListener('focusout',event => { focused = !!event.relatedTarget && host.contains(event.relatedTarget) && !button?.contains(event.relatedTarget); update(); },options);
    button?.addEventListener('click',event => { event.preventDefault(); event.stopPropagation(); pinned = !pinned; hover = false; focused = false; update(); },options);
    return () => { ++sequence; controller.abort(); cancel(); };
  }
  function mount(scope = document) {
    scope.querySelectorAll('[data-rs2-carousel], [data-rs2-stitch]').forEach(host => { if (!mounted.has(host)) mounted.set(host,host.hasAttribute('data-rs2-carousel') ? carousel(host) : stitch(host)); });
  }
  // Observe only visual hooks, so Shopify's section replacements need no commerce patch.
  const observer = new MutationObserver(records => {
    for (const record of records) for (const node of record.removedNodes) if (node.nodeType === 1) {
      [node,...node.querySelectorAll('[data-rs2-carousel], [data-rs2-stitch]')].forEach(host => { mounted.get(host)?.(); mounted.delete(host); });
    }
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) {
      if (node.matches('[data-rs2-carousel], [data-rs2-stitch]') && !mounted.has(node)) mounted.set(node,node.hasAttribute('data-rs2-carousel') ? carousel(node) : stitch(node));
      mount(node);
    }
  });
  let purchaseCleanup = () => {};
  const submissions = new Map();
  document.addEventListener('submit',event => {
    const form = event.target;
    if (!form.closest('product-form')) return;
    const button = event.submitter || form.querySelector('[type="submit"]');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    const variant = new FormData(form).get('id');
    if (variant) submissions.set(String(variant),{button,time:performance.now()});
  },true);
  function purchaseFeedback(button) {
    purchaseCleanup();
    const target = document.querySelector('#cart-icon-bubble');
    if (motion.matches || !target || !button.isConnected || !button.animate) return;
    const start = button.getBoundingClientRect(), end = target.getBoundingClientRect();
    const x = start.left + start.width / 2, y = start.top + start.height / 2;
    const dx = end.left + end.width / 2 - x, dy = end.top + end.height / 2 - y;
    const pixels = [], animations = [];
    let timer;
    purchaseCleanup = () => { clearTimeout(timer); animations.forEach(animation => animation.cancel()); pixels.forEach(pixel => pixel.remove()); };
    [-8,0,8].forEach((offset,i) => {
      const pixel = document.createElement('span'); pixel.className = 'rs2-purchase-pixel'; pixel.setAttribute('aria-hidden','true'); pixel.style.left = `${x + offset}px`; pixel.style.top = `${y}px`; document.body.append(pixel); pixels.push(pixel);
      const animation = pixel.animate([{transform:'translate(-50%,-50%)',opacity:1},{transform:`translate(${dx * .45}px,${dy * .45 - 40}px)`,opacity:1,offset:.45},{transform:`translate(${dx - offset}px,${dy}px) scale(.5)`,opacity:0}],{duration:560,easing:'cubic-bezier(.2,.6,.3,1)'});
      animations.push(animation); animation.onfinish = () => { pixel.remove(); if (i === 1 && target.isConnected && !motion.matches) animations.push(target.animate([{transform:'scale(1)'},{transform:'scale(1.12)'},{transform:'scale(1)'}],{duration:240})); };
    });
    timer = setTimeout(purchaseCleanup,900);
  }
  function variantSpace(rows, width) {
    const perLine = Math.max(1, Math.floor((width - 11) / 56));
    return rows.reduce((height, [type, count], index) => height + (index ? 12 : 0) +
      (type === 'image' ? 98 : 93) + Math.max(0, Math.ceil(Number(count) / perLine) - 1) * 54, 0);
  }
  function reserveVariants() {
    document.querySelectorAll('[data-rs2-variant-rows]').forEach(slot => {
      const picker = slot.querySelector('variant-selects');
      if (!picker || getComputedStyle(picker).display !== 'none') return;
      const rows = slot.dataset.rs2VariantRows.split(',').filter(Boolean).map(row => row.split(':'));
      slot.dataset.rs2Reserve = '';
      slot.style.setProperty('--rs2-option-space', `${variantSpace(rows, slot.clientWidth)}px`);
    });
  }
  // A viewport curtain leaves the complete shop painted and unmasked underneath.
  function revealPage(boot) {
    stopEntry();
    if (motion.matches || document.hidden) { boot.finish(); return; }
    const curtain = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    curtain.id = 'rs2-entry-curtain';
    curtain.classList.add('rs2-entry-curtain');
    curtain.setAttribute('aria-hidden', 'true');
    curtain.setAttribute('focusable', 'false');
    const pixels = ranks.map((rank, i) => `<rect x="${i % 4 * 2}" y="${Math.floor(i / 4) * 2}" width="2" height="2" data-rs2-rank="${rank}" style="--rank:${rank}"/>`).join('');
    curtain.innerHTML = `<defs><pattern id="rs2-entry-pattern" width="8" height="8" patternUnits="userSpaceOnUse">${pixels}</pattern></defs><rect width="100%" height="100%" fill="url(#rs2-entry-pattern)"/>`;
    let frame = 0, watchdog = 0, stopped = false;
    const cleanup = () => {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(frame);
      clearTimeout(watchdog);
      curtain.remove();
      stopEntry = () => {};
    };
    stopEntry = cleanup;
    curtain.addEventListener('animationend', event => { if (event.target.getAttribute('data-rs2-rank') === '15') cleanup(); });
    document.body.append(curtain);
    watchdog = setTimeout(cleanup, 1400);
    boot.finish();
    // Commit the ready page behind the solid curtain before starting native CSS motion.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        if (stopped) return;
        curtain.classList.add('is-revealing');
        if (getComputedStyle(curtain.querySelector('[data-rs2-rank="15"]')).animationName === 'none') cleanup();
      });
    });
  }
  async function enterPage() {
    // Dawn's delayed slide-in must settle underneath the page-entry pixels.
    document.querySelectorAll('.scroll-trigger').forEach(element => {
      const bounds = element.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < window.innerHeight && bounds.right > 0 && bounds.left < window.innerWidth) element.classList.add('rs2-entry-ready');
    });
    const boot = window.rs2PageBoot;
    if (!boot?.active) return;
    const images = [...document.images].filter(image => {
      if (image.classList.contains('rs2-card-gallery__hover-detail')) return false;
      if (image.closest('[hidden], [inert], [aria-hidden="true"], cart-drawer:not(.rs2-drawer-open), header-drawer:not(.rs2-drawer-open) .rs2-menu')) return false;
      const bounds = image.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < window.innerHeight && bounds.right > 0 && bounds.left < window.innerWidth;
    });
    const ready = images.map(image => {
      image.loading = 'eager';
      return typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
    });
    if (document.fonts) ready.push(document.fonts.load('28px Tiny5').catch(() => {}));
    let deadline;
    await Promise.race([
      Promise.all(ready),
      new Promise(resolve => { deadline = setTimeout(resolve, 1200); })
    ]);
    clearTimeout(deadline);
    if (!boot.active) return;
    try { revealPage(boot); }
    catch (_) { stopEntry(); boot.finish(); }
  }

  function init() {
    syncAppearance(); reserveVariants(); mount(); observer.observe(document.body,{childList:true,subtree:true});
    enterPage();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate,event => {
        if (event.source !== 'product-form' || !event.cartData || event.cartData.status) return;
        const key = String(event.productVariantId), submission = submissions.get(key); submissions.delete(key);
        if (submission && performance.now() - submission.time < 30000) purchaseFeedback(submission.button);
      });
      subscribe(PUB_SUB_EVENTS.cartError,event => { if (event.source === 'product-form') submissions.delete(String(event.productVariantId)); });
    }
  }
  motion.addEventListener('change',() => { if (motion.matches) { stopEntry(); [...activeEffects].forEach(finish => finish()); purchaseCleanup(); } });
  document.addEventListener('visibilitychange', () => { if (document.hidden) { stopEntry(); [...activeEffects].forEach(finish => finish()); } });
  window.addEventListener('pagehide',() => { stopEntry(); stopTheme(); [...reveals.values()].forEach(cancel => cancel()); [...activeEffects].forEach(finish => finish()); purchaseCleanup(); });
  window.addEventListener('resize', reserveVariants);
  document.addEventListener('shopify:section:load',() => { syncAppearance(); reserveVariants(); mount(); const drawer = document.querySelector('cart-drawer'); if (drawer && typeof drawer.setHeaderCartIconAccessibility === 'function') drawer.setHeaderCartIconAccessibility(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',init,{once:true}); else init();
})();
